import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { TaskSubmission, TaskAttachment, VoiceType } from '../types/api';
import { useToast } from '../hooks/useToast';
import { formatTime } from '../utils/format';

const STATUS_PRESET: Record<string, { label: string; tone: string }> = {
  pending: { label: '排队中', tone: 'muted' },
  generating_script: { label: '生成口播稿', tone: 'processing' },
  script_generated: { label: '脚本完成', tone: 'success' },
  synthesizing_audio: { label: '生成音频', tone: 'processing' },
  succeeded: { label: '已完成', tone: 'success' },
  completed: { label: '已完成', tone: 'success' },
  failed: { label: '失败', tone: 'error' },
  processing: { label: '处理中', tone: 'processing' },
};

function describeVoice(voice?: VoiceType): string {
  if (!voice) return '未命名音色';
  const name = voice.display_name || voice.voice_name || '未命名音色';
  const tags: string[] = [];
  if (voice.gender === 'male') tags.push('男声');
  if (voice.gender === 'female') tags.push('女声');
  if (voice.language === 'zh') tags.push('中文');
  if (voice.language === 'en') tags.push('英文');
  return tags.length ? `${name}（${tags.join('/')}）` : name;
}

function getStatusColor(tone: string): string {
  switch (tone) {
    case 'success': return 'success';
    case 'error': return 'danger';
    case 'processing': return 'primary';
    default: return 'secondary';
  }
}

export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [task, setTask] = useState<TaskSubmission | null>(null);
  const [voices, setVoices] = useState<VoiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    api.getVoiceTypes().then((res) => setVoices(res.voice_types || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    const taskId = Number(id);
    if (isNaN(taskId)) return;

    const loadTask = async () => {
      setLoading(true);
      try {
        const result = await api.getTaskSubmission(taskId);
        const voice = voices.find((v) => v.voice_type === result.voice_type);
        setTask({
          ...result,
          status_label: (STATUS_PRESET[result.status] || { label: result.status, tone: 'muted' }).label,
          status_tone: (STATUS_PRESET[result.status] || { label: result.status, tone: 'muted' }).tone,
          voice_display_name: describeVoice(voice),
          created_at_display: formatTime(result.created_at),
          finished_at_display: result.finished_at ? formatTime(result.finished_at) : '',
        });
      } catch {
        showToast('加载任务详情失败', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadTask();
  }, [id]);

  const handleCopyScript = async () => {
    if (!task?.script_text) return;
    try {
      await navigator.clipboard.writeText(task.script_text);
      showToast('口播稿已复制', 'success');
    } catch {
      showToast('复制失败', 'error');
    }
  };

  const handleDownloadAttachment = async (attachment: TaskAttachment) => {
    try {
      const blob = await api.downloadAttachment(attachment.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.original_filename || `attachment_${attachment.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showToast('附件已下载', 'success');
    } catch {
      showToast('下载附件失败', 'error');
    }
  };

  const handleDownloadAudio = () => {
    if (!task?.audio_url) return;
    window.open(task.audio_url, '_blank');
    showToast('正在下载音频...', 'success');
  };

  const handleRetry = async () => {
    if (!task) return;
    setRetrying(true);
    try {
      await api.retryTask(task.id);
      showToast('任务已重新提交', 'success');
      const result = await api.getTaskSubmission(task.id);
      const statusMeta = STATUS_PRESET[result.status] || { label: result.status, tone: 'muted' };
      const voice = voices.find((v) => v.voice_type === result.voice_type);
      setTask({
        ...result,
        status_label: statusMeta.label,
        status_tone: statusMeta.tone,
        voice_display_name: describeVoice(voice),
        created_at_display: formatTime(result.created_at),
        finished_at_display: result.finished_at ? formatTime(result.finished_at) : '',
      });
    } catch {
      showToast('重试任务失败', 'error');
    } finally {
      setRetrying(false);
    }
  };

  if (!id) {
    return (
      <div className="page-container">
        <div className="empty-state text-center">
          <i className="fas fa-exclamation-triangle fa-3x text-warning"></i>
          <h2 className="mt-3 text-fg">任务不存在</h2>
          <p className="text-muted">请返回任务列表选择其他任务</p>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/tasks')}>返回列表</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
          <p className="mt-3 text-muted">正在加载任务详情...</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="page-container">
        <div className="empty-state text-center">
          <i className="fas fa-exclamation-triangle fa-3x text-warning"></i>
          <h2 className="mt-3 text-fg">加载失败</h2>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/tasks')}>返回列表</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="glass-card mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="mb-1">{task.title || '未命名任务'}</h3>
            <small className="text-muted">{task.id ? `任务ID：${task.id}` : ''}</small>
          </div>
          <span className={`badge bg-${getStatusColor(task.status_tone || 'muted')}`}>
            {task.status_label || task.status}
          </span>
        </div>
        <div className="text-muted mt-3">
          创建：{task.created_at_display || '-'}
          {task.finished_at_display && ` • 完成：${task.finished_at_display}`}
        </div>
      </div>

      <div className="glass-card mb-4">
        <h5 className="mb-3">任务概览</h5>
        <div className="row">
          <div className="col-md-4 mb-3">
            <div className="fw-bold text-muted">口播稿风格</div>
            <div>{task.style || '-'}</div>
          </div>
          <div className="col-md-4 mb-3">
            <div className="fw-bold text-muted">音色</div>
            <div>{task.voice_display_name || '未命名音色'}</div>
          </div>
          <div className="col-md-4 mb-3">
            <div className="fw-bold text-muted">字数</div>
            <div>{task.script_word_count ?? 0} 字</div>
          </div>
        </div>
      </div>

      {task.status === 'failed' && task.error_message && (
        <div className="glass-card mb-4">
          <div className="text-danger fw-bold mb-2">
            <i className="fas fa-exclamation-triangle me-2"></i>错误信息
          </div>
          <p className="text-fg">{task.error_message}</p>
        </div>
      )}

      {task.script_text && (
        <div className="glass-card mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">口播稿</h5>
            <button className="btn btn-sm btn-outline-secondary" onClick={handleCopyScript}>
              <i className="fas fa-copy me-1"></i>复制全文
            </button>
          </div>
          <pre className="script-preview" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {task.script_text}
          </pre>
        </div>
      )}

      {task.attachments && task.attachments.length > 0 && (
        <div className="glass-card mb-4">
          <h5 className="mb-3">原始文档</h5>
          <div className="list-group">
            {task.attachments.map((att) => (
              <div key={att.id} className="list-group-item d-flex justify-content-between align-items-center border-0 py-3">
                <div>
                  <div className="fw-bold">{att.original_filename}</div>
                  <div className="text-muted small">
                    上传时间：{att.created_at ? formatTime(att.created_at) : '-'}
                    {att.word_count ? ` • ${att.word_count} 字` : ''}
                  </div>
                </div>
                <button className="btn btn-sm btn-outline-primary" onClick={() => handleDownloadAttachment(att)}>
                  <i className="fas fa-download me-2"></i>下载
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {task.audio_url && (
        <div className="glass-card mb-4">
          <h5 className="mb-3">音频文件</h5>
          <audio className="w-100" controls preload="none" src={task.audio_url}>
            您的浏览器不支持音频播放。
          </audio>
          <div className="mt-3">
            <button className="btn btn-outline-success btn-sm" onClick={handleDownloadAudio}>
              <i className="fas fa-download me-2"></i>下载音频
            </button>
          </div>
        </div>
      )}

      {task.status === 'failed' && (
        <div className="glass-card">
          <div className="d-grid">
            <button className="btn btn-outline-warning" disabled={retrying} onClick={handleRetry}>
              {retrying ? (
                <><i className="fas fa-spinner fa-spin me-2"></i>重试中...</>
              ) : (
                <><i className="fas fa-redo me-2"></i>重新执行任务</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}